import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useReducer
} from 'react';
import { authAPI, adminAPI, settingsAPI } from '../services/api.js';
import { setTenantTimeZone } from '../lib/tz.js';
import {
  clearStoredAuth,
  getStoredTenant,
  getStoredToken,
  getStoredUser,
  getStoredSelectedTenant,
  setStoredSelectedTenant,
  getStoredDefaultTenant,
  setStoredDefaultTenant,
  setStoredTenant,
  setStoredToken,
  setStoredUser
} from '../services/tokenStore.js';

const AuthContext = createContext(null);

// Resolve the tenant an owner should LAND on, and make the session consistent
// with it, for BOTH entry points (fresh magic-link verify and cached-session
// bootstrap). Kept as one function on purpose: when only one path seeded the
// X-Tenant-Id source, a fresh login showed the default tenant in the UI while
// every API call — including writes — still hit the login tenant.
//
// Order: an in-session switch → the owner's saved default → the login tenant.
// Returns the resolved tenant and primes the display timezone as a side effect.
function primeOwnerLanding(user, tenant) {
  if (!user?.isPlatformOwner) {
    setTenantTimeZone(tenant?.timezone);
    return tenant;
  }
  const sessionSel = getStoredSelectedTenant();
  const selected = sessionSel || getStoredDefaultTenant(user.email) || tenant;
  // Seed sessionStorage (the X-Tenant-Id source api.js reads) when it's the
  // DEFAULT — not an in-session switch — that put us off the login tenant, so the
  // very first request carries the right tenant header.
  if (!sessionSel && selected?.id && selected.id !== tenant?.id) {
    setStoredSelectedTenant(selected);
  }
  setTenantTimeZone(selected?.timezone);
  return selected;
}

const initialState = {
  user: null,
  tenant: null,
  token: null,
  loading: true,
  isAuthenticated: false,
  error: null,
  // Platform-owner extras (no effect for normal tenant users).
  isOwner: false,
  availableTenants: [],
  // The owner's saved default (home) tenant id, or null. Drives the switcher's
  // "★ default" indicator; the actual landing selection is resolved at bootstrap.
  defaultTenantId: null,
  // The tenant the owner is currently viewing. Defaults to their own login
  // tenant; persisted so it survives the hard reload on switch.
  selectedTenant: null
};

function reducer(state, action) {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'BOOTSTRAP_SUCCESS': {
      const isOwner = !!action.payload.user?.isPlatformOwner;
      const stored = getStoredSelectedTenant();
      // Owner landing tenant, in order: an in-session switch, then their saved
      // DEFAULT (localStorage, survives the session), then their login tenant.
      // The default is why the console stops reverting to the login tenant every
      // session. Everyone else is pinned to their own tenant.
      const selectedTenant = isOwner
        ? stored || getStoredDefaultTenant(action.payload.user?.email) || action.payload.tenant
        : action.payload.tenant;
      return {
        ...state,
        user: action.payload.user,
        tenant: action.payload.tenant,
        token: action.payload.token,
        isAuthenticated: true,
        loading: false,
        error: null,
        isOwner,
        selectedTenant,
        defaultTenantId: isOwner
          ? getStoredDefaultTenant(action.payload.user?.email)?.id || null
          : null
      };
    }
    case 'SET_DEFAULT_TENANT':
      return { ...state, defaultTenantId: action.payload };
    case 'SET_TENANTS':
      return { ...state, availableTenants: action.payload };
    case 'LOGOUT':
      return { ...initialState, loading: false };
    case 'ERROR':
      return { ...state, error: action.payload, loading: false };
    case 'UPDATE_TENANT': {
      // A settings write lands on the tenant the app is VIEWING — which for an
      // owner who has switched is NOT their login tenant. Merge into whichever
      // of the two copies describes that tenant, and leave the other alone:
      // updating `tenant` blindly would write the viewed tenant's settings onto
      // the owner's own, and updating only `tenant` would leave `selectedTenant`
      // (what consumers read) showing the pre-edit value.
      const { id, updates } = action.payload;
      return {
        ...state,
        tenant: state.tenant?.id === id ? { ...state.tenant, ...updates } : state.tenant,
        selectedTenant:
          state.selectedTenant?.id === id
            ? { ...state.selectedTenant, ...updates }
            : state.selectedTenant
      };
    }
    default:
      return state;
  }
}

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  // Hydrate from sessionStorage on mount. We do not re-hit the network here
  // because there's no /me endpoint yet — the token is treated as valid until
  // a real request returns 401, at which point the 'auth:unauthorized'
  // listener below kicks us out.
  useEffect(() => {
    const token = getStoredToken();
    const user = getStoredUser();
    const tenant = getStoredTenant();

    if (token && user) {
      // Resolve landing + seed X-Tenant-Id + prime tz before dispatch, so the
      // first page fetch already carries the right tenant header and no timestamp
      // flashes in UTC.
      primeOwnerLanding(user, tenant);
      dispatch({
        type: 'BOOTSTRAP_SUCCESS',
        payload: { token, user, tenant }
      });
    } else {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, []);

  // Global 401 handler — api.js dispatches this from any failing request.
  useEffect(() => {
    const onUnauthorized = () => {
      clearStoredAuth();
      dispatch({ type: 'LOGOUT' });
    };
    window.addEventListener('auth:unauthorized', onUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', onUnauthorized);
  }, []);

  // Re-read the tenant's timezone from the server once we're authed.
  //
  // The cached tenant blob can't be trusted for this: sessions are never
  // revalidated (there's no /me endpoint), so a session that logged in before the
  // zone existed carries no zone at all — and we'd read that absence as "never
  // chosen" and block scheduling on an account that has a perfectly good zone
  // server-side. The blob still primes the singleton first (no UTC flash); this
  // just corrects it a beat later. X-Tenant-Id rides along, so a switched owner
  // gets the zone of the tenant they're actually looking at.
  useEffect(() => {
    if (!state.isAuthenticated) return;
    const id = state.selectedTenant?.id || state.tenant?.id;
    if (!id) return;
    let cancelled = false;
    settingsAPI
      .getTimezone()
      .then((r) => {
        if (cancelled) return;
        const tz = r?.data?.timezone ?? null;
        // Only dispatch on a real change — this runs on every mount.
        const known = state.selectedTenant?.id === id ? state.selectedTenant : state.tenant;
        if ((known?.timezone ?? null) === tz) return;
        setTenantTimeZone(tz);
        dispatch({ type: 'UPDATE_TENANT', payload: { id, updates: { timezone: tz } } });
      })
      .catch(() => {
        /* route not deployed / offline — keep whatever the cached blob gave us */
      });
    return () => {
      cancelled = true;
    };
  }, [state.isAuthenticated, state.selectedTenant?.id, state.tenant?.id]);

  // Platform owners: load the full tenant list for the switcher. Refetch on auth AND on
  // window focus, so a newly-created/assigned tenant shows up without a re-login (the
  // list was previously fetched only once and went stale).
  useEffect(() => {
    if (!state.isAuthenticated || !state.isOwner) return;
    const loadTenants = () =>
      adminAPI
        .listTenants()
        .then((r) => {
          const list = r?.data || [];
          dispatch({ type: 'SET_TENANTS', payload: list });
          // ⛔ RE-HYDRATE THE VIEWED TENANT FROM THE FRESH LIST.
          //
          // `selectedTenant` is restored from a sessionStorage/localStorage BLOB
          // captured whenever the owner last switched. Nothing ever refreshed it, so
          // every field in it — `features` above all — was frozen at that moment.
          //
          // That is not theoretical: Gary Southers was given
          // `features.template = 'family-care'` in the DB, and the sidebar kept
          // rendering the full super-admin menu (Sales, Employee, Demonstrations,
          // Admin) because the cached blob still said `features: {}`, and
          // `resolveTemplateKey({}, isOwner)` falls through to 'super-admin'. No
          // reload fixed it — only re-picking the tenant in the switcher would, and
          // nothing on screen said so.
          //
          // The same staleness applies to companyName, plan and status. This is the
          // general fix; the existing timezone re-prime below is the special case
          // that was already noticed.
          const viewedId = state.selectedTenant?.id || state.tenant?.id;
          const fresh = viewedId && list.find((t) => t.id === viewedId);
          if (fresh) {
            dispatch({ type: 'UPDATE_TENANT', payload: { id: viewedId, updates: fresh } });
            // Rewrite the stored blob too, but ONLY if one already exists — creating a
            // stored selection for an owner who never switched would pin them to a
            // tenant they didn't choose. Without this the in-memory fix is re-applied
            // on every load, which works, but leaves the persisted copy lying.
            if (getStoredSelectedTenant()?.id === viewedId) setStoredSelectedTenant(fresh);
          }
          // Self-heal a stale default: if the saved default tenant has been
          // retired or is otherwise gone from the list, clear it so the next
          // session lands on the login tenant instead of a dead X-Tenant-Id.
          // Only act on a non-empty list (an empty one means the fetch failed).
          const defId = getStoredDefaultTenant(state.user?.email)?.id;
          if (defId && list.length && !list.some((t) => t.id === defId && t.status !== 'deleted')) {
            setStoredDefaultTenant(null, state.user?.email);
            dispatch({ type: 'SET_DEFAULT_TENANT', payload: null });
          }
        })
        .catch(() => {
          /* not an owner / endpoint unavailable — leave the list empty */
        });
    loadTenants();
    window.addEventListener('focus', loadTenants);
    return () => window.removeEventListener('focus', loadTenants);
  }, [state.isAuthenticated, state.isOwner, state.user?.email, state.selectedTenant?.id, state.tenant?.id]);

  // Switch the owner into another tenant. We persist the choice and hard-reload
  // so EVERY page refetches under the new X-Tenant-Id header — simpler and more
  // reliable than threading a tenant id through every in-flight query.
  const switchTenant = useCallback((tenant) => {
    if (!tenant?.id) return;
    setStoredSelectedTenant(tenant);
    window.location.assign('/');
  }, []);

  // Owner: make `tenant` the tenant this console lands on every session. Stored in
  // localStorage so it outlives the tab; takes effect on the next load (bootstrap
  // reads it). Pass null to clear back to "land on my login tenant".
  const setDefaultTenant = useCallback(
    (tenant) => {
      setStoredDefaultTenant(tenant || null, state.user?.email);
      dispatch({ type: 'SET_DEFAULT_TENANT', payload: tenant?.id || null });
    },
    [state.user?.email]
  );

  const sendMagicLink = useCallback(async (email) => {
    try {
      await authAPI.sendMagicLink(email);
      return { success: true };
    } catch (err) {
      return {
        success: false,
        error: err.body?.message || err.message || 'Failed to send magic link'
      };
    }
  }, []);

  const verifyMagicLink = useCallback(async (token) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const result = await authAPI.verifyMagicLink(token);
      // core/ returns { success, token, user, tenant }
      if (!result?.success || !result?.token) {
        throw new Error(result?.message || 'Invalid magic link');
      }
      setStoredToken(result.token);
      setStoredUser(result.user);
      setStoredTenant(result.tenant);
      // Same resolve+seed as bootstrap. An owner with a saved default lands on it
      // even on a brand-new login — and, critically, the X-Tenant-Id source is
      // seeded here too, so API reads AND writes scope to the tenant the UI shows
      // rather than silently to the login tenant.
      primeOwnerLanding(result.user, result.tenant);
      dispatch({
        type: 'BOOTSTRAP_SUCCESS',
        payload: {
          token: result.token,
          user: result.user,
          tenant: result.tenant
        }
      });
      return { success: true };
    } catch (err) {
      dispatch({ type: 'SET_LOADING', payload: false });
      return {
        success: false,
        error: err.body?.message || err.message || 'Verification failed'
      };
    }
  }, []);

  const logout = useCallback(() => {
    clearStoredAuth();
    dispatch({ type: 'LOGOUT' });
  }, []);

  // Apply a settings change to the tenant currently being VIEWED.
  //
  // That is `selectedTenant` — the tenant whose id went out as X-Tenant-Id and
  // whose row the server just wrote. For everyone except a switched owner it is
  // also the login tenant. Both cached blobs are rehydrated by bootstrap on the
  // next load, so whichever one describes the edited tenant has to be updated
  // here; miss it and the save silently reverts on refresh while the server keeps
  // the new value.
  const updateTenant = useCallback(
    (updates) => {
      const editedId = state.selectedTenant?.id || state.tenant?.id;
      if (!editedId) return;

      const login = getStoredTenant();
      if (login?.id === editedId) setStoredTenant({ ...login, ...updates });
      const selected = getStoredSelectedTenant();
      if (selected?.id === editedId) setStoredSelectedTenant({ ...selected, ...updates });

      // Re-render every timestamp in the new zone straight away — the tz
      // singleton isn't reactive, so it must be set before the dispatch.
      if ('timezone' in updates) setTenantTimeZone(updates.timezone);
      dispatch({ type: 'UPDATE_TENANT', payload: { id: editedId, updates } });
    },
    [state.selectedTenant?.id, state.tenant?.id]
  );

  // The tenant actually on screen — the login tenant for everyone except an owner
  // who has switched. Its zone is the one the app renders and schedules in.
  const shownTenant = (state.isOwner && state.selectedTenant) || state.tenant;

  const value = {
    ...state,
    // null = this account has never chosen a zone. Scheduling is blocked until it
    // does (the server enforces it; the UI just says so first). Don't paper over
    // the null — that's the whole point of forcing the choice.
    tenantTimeZone: shownTenant?.timezone ?? null,
    sendMagicLink,
    verifyMagicLink,
    logout,
    updateTenant,
    switchTenant,
    setDefaultTenant
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
